import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);
const STORAGE_KEY = 'hebeh_auth_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed, obj = authed
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || '');

  // Axios interceptor: attach token
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem(STORAGE_KEY);
      if (t && config.url?.startsWith(API)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    const resId = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          const url = err?.config?.url || '';
          if (!url.includes('/api/auth/login')) {
            localStorage.removeItem(STORAGE_KEY);
            setToken('');
            setUser(false);
          }
        }
        return Promise.reject(err);
      }
    );

    // Fetch interceptor: attach token to ALL window.fetch() calls targeting our API
    // (muchos componentes usan fetch() nativo en vez de axios, sin este wrapper
    // los requests saldrían sin JWT y recibirían 401 silencioso).
    const originalFetch = window.fetch;
    window.fetch = async function patchedFetch(input, init = {}) {
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (url.startsWith(API)) {
          const t = localStorage.getItem(STORAGE_KEY);
          if (t) {
            const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));
            if (!headers.has('Authorization')) {
              headers.set('Authorization', `Bearer ${t}`);
            }
            init = { ...init, headers };
          }
        }
      } catch (e) {
        // si algo falla en el wrapper, caer al fetch original
      }
      const res = await originalFetch(input, init);
      // Auto-logout en 401 (excepto en el propio endpoint de login)
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (res.status === 401 && url.startsWith(API) && !url.includes('/api/auth/login')) {
          localStorage.removeItem(STORAGE_KEY);
          setToken('');
          setUser(false);
        }
      } catch (e) { /* noop */ }
      return res;
    };

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
      window.fetch = originalFetch;
    };
  }, []);

  // Check existing session
  useEffect(() => {
    let alive = true;
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) { setUser(false); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
        if (!alive) return;
        if (r.ok) {
          const data = await r.json();
          setUser(data.user || false);
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setUser(false);
        }
      } catch {
        if (alive) setUser(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const login = useCallback(async (email, password, mfaCode = '') => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);
    let r, data;
    try {
      r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, mfa_code: mfaCode }),
        signal: controller.signal,
      });
      data = await r.json();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        return { ok: false, error: 'El servidor no respondió a tiempo (25s). Intenta de nuevo.' };
      }
      return { ok: false, error: err.message || 'Error de red. Verifica tu conexión.' };
    }
    clearTimeout(timeoutId);
    if (!r.ok) {
      return { ok: false, error: data.detail || data.error || 'Error al iniciar sesión' };
    }
    if (data.mfa_required) {
      return { ok: false, mfa_required: true, message: data.message };
    }
    if (data.success && data.token) {
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      return { ok: true };
    }
    return { ok: false, error: 'Respuesta inválida del servidor' };
  }, []);

  const logout = useCallback(() => {
    try {
      const t = localStorage.getItem(STORAGE_KEY);
      if (t) {
        fetch(`${API}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${t}` },
        }).catch(() => {});
      }
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setToken('');
      setUser(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
